import { Info, Link2 } from "lucide-react";
import { useAppLocale } from "@/features/i18n/LocaleProvider";
import type { BlockT, BlockTextStyleT } from "@patient-watch/types";
import { RichText } from "@patient-watch/forms";
import { cn } from "@/lib/utils";
import Call from "@/components/common/Call";
import { localizeBlock } from "@/features/blocks/utils/localizeBlock";
import { toVideoEmbedUrl, toVideoExternalUrl } from "@/features/blocks/utils/toVideoEmbedUrl";
import ImageCarouselBlock from "./ImageCarouselBlock";

interface Props {
    block: BlockT;
}

const textStyleClass: Record<Exclude<BlockTextStyleT, "callout">, string> = {
    paragraph: "text-slate-700 dark:text-slate-300 text-lg leading-relaxed text-pretty",
    lead: "font-medium text-slate-800 dark:text-slate-200 text-lg leading-relaxed text-pretty",
    small: "text-slate-500 dark:text-slate-400 text-md leading-relaxed text-pretty",
    heading: "-mb-2 font-semibold text-slate-900 dark:text-slate-100 text-lg tracking-tight",
};

function TextBlock({ block }: Props) {
    if (!block.content) return null;
    const style = block.text_style ?? "callout";
    if (style === "callout") {
        return <Call icon={<Info size={16} />} words={block.content} size="sm" variant="muted" className="mt-0" />;
    }
    const text = (
        <RichText
            className={cn(
                textStyleClass[style],
                block.text_align === "center" && "text-center",
                block.text_align === "right" && "text-right"
            )}
        >
            {block.content}
        </RichText>
    );
    // Small text steps down to text-md, so it opts out of the form-wide text-lg (index.css).
    return style === "small" ? <div data-own-text-size>{text}</div> : text;
}

function ImageBlock({ block }: Props) {
    const { locale } = useAppLocale();
    const fallbackAlt =
        locale === "de-DE"
            ? "Formularbild"
            : locale === "da-DK"
              ? "Formularbillede"
              : locale === "fr-FR"
                ? "Image du formulaire"
                : locale === "it-IT"
                  ? "Immagine del modulo"
                  : locale === "es-ES"
                    ? "Imagen del formulario"
                    : "Form image";
    if (!block.media_url) return null;
    return (
        <div data-anno="block-image" className="space-y-2">
            <img
                src={block.media_url}
                alt={block.caption ?? fallbackAlt}
                className="block mx-auto rounded-md max-w-full h-auto"
            />
            {block.caption ? <p className="text-muted-foreground text-lg">{block.caption}</p> : null}
        </div>
    );
}

function VideoBlock({ block }: Props) {
    const { locale } = useAppLocale();
    const copy =
        locale === "fr-FR"
            ? { fallbackTitle: "Vidéo du formulaire", openNewTab: "Ouvrir la vidéo dans un nouvel onglet" }
            : locale === "it-IT"
              ? { fallbackTitle: "Video del modulo", openNewTab: "Apri il video in una nuova scheda" }
              : locale === "es-ES"
                ? { fallbackTitle: "Vídeo del formulario", openNewTab: "Abrir el vídeo en una pestaña nueva" }
                : locale === "de-DE"
                  ? { fallbackTitle: "Formularvideo", openNewTab: "Video in neuem Tab öffnen" }
                  : locale === "da-DK"
                    ? { fallbackTitle: "Formularvideo", openNewTab: "Åbn video i ny fane" }
                    : { fallbackTitle: "Form video", openNewTab: "Open video in new tab" };

    if (!block.media_url) return null;
    const embedUrl = toVideoEmbedUrl(block.media_url);
    const externalUrl = toVideoExternalUrl(block.media_url);
    return (
        <div data-anno="block-video" className="space-y-2">
            <div className="aspect-video">
                <iframe
                    src={embedUrl}
                    title={block.caption ?? copy.fallbackTitle}
                    className="border-0 rounded-md w-full h-full"
                    loading="lazy"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                />
            </div>
            <div className="flex items-center gap-2">
                <Link2 className="w-4 h-4 text-muted-foreground" />
                <a
                    href={externalUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-primary text-lg underline underline-offset-2"
                >
                    {copy.openNewTab}
                </a>
            </div>
            {block.caption ? <p className="text-muted-foreground text-lg">{block.caption}</p> : null}
        </div>
    );
}

export function BlockSwitch({ block }: Props) {
    const { locale } = useAppLocale();
    const localized = localizeBlock(block, locale);

    switch (localized.block_type) {
        case "text":
            return <TextBlock block={localized} />;
        case "image":
            return <ImageBlock block={localized} />;
        case "video":
            return <VideoBlock block={localized} />;
        case "image-carousel":
            return <ImageCarouselBlock block={localized} />;
        default:
            return null;
    }
}
